import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { getAuthHeaders, useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { 
  ArrowLeft, Check, Sparkles, Building2, Loader2, 
  IndianRupee, Users, Film, Crown, Zap
} from 'lucide-react';
import { toast } from 'sonner';
import { API_BASE } from '../config/api';
import { WORKSPACE_PRICES } from '../config/pricing';

const API = API_BASE;

// Pricing version for all NEW workspace-purchase checkouts (matches
// UpgradeWorkspaceModal — see PHASE_0_3_BUILD_SPEC.md Task A).
// v1 pricing was retired (V1_PRICING_CLEANUP_PLAN.md) — the backend serves a
// single price book and create-subscription no longer takes a pricing_version.

// Razorpay script loader
const loadRazorpayScript = () => {
  return new Promise((resolve) => {
    if (window.Razorpay) {
      resolve(true);
      return;
    }
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
};

const WORKSPACE_TYPES = [
  {
    type: 'pro',
    name: 'Pro Workspace',
    description: 'For independent filmmakers and small teams',
    icon: Zap,
    color: 'text-blue-500',
    bgColor: 'bg-blue-500/10',
    features: [
      '5 team seats included',
      '3 active films',
      'Unlimited scenes & shots',
      'Script upload & parsing',
      'Team collaboration',
      'Export & reports',
    ]
  },
  {
    type: 'studio',
    name: 'Studio Workspace',
    description: 'For studios and large production companies',
    icon: Crown,
    color: 'text-amber-500',
    bgColor: 'bg-amber-500/10',
    popular: true,
    features: [
      '15 team seats included',
      '10 active films',
      'Unlimited scenes & shots',
      'Script upload & parsing',
      'Team collaboration',
      'Export & reports',
      'Advanced analytics',
      'Advanced governance',
      'Priority support',
    ]
  }
];

const WorkspacePurchase = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [loading, setLoading] = useState(false);
  const [processingType, setProcessingType] = useState(null);
  const [razorpayConfig, setRazorpayConfig] = useState(null);
  const [checkingPending, setCheckingPending] = useState(true);

  useEffect(() => {
    // Check for existing pending purchase first
    const checkPendingPurchase = async () => {
      try {
        const response = await axios.get(`${API}/razorpay/workspace/pending-purchase`, {
          headers: getAuthHeaders()
        });
        if (response.data.has_pending && response.data.purchase) {
          // User already has a pending purchase - redirect to create
          toast.info('You have an unused workspace purchase. Redirecting to complete setup...');
          navigate(`/workspace/create?purchase_id=${response.data.purchase.id}`, { replace: true });
          return;
        }
      } catch (err) {
        // Silently ignore: pending-purchase lookup is best-effort and any
        // failure (offline, 5xx) should not block the buy flow itself.
        if (import.meta.env.DEV) console.warn('[WorkspacePurchase] pending-purchase check failed:', err);
      }
      setCheckingPending(false);
    };
    
    checkPendingPurchase();
    fetchRazorpayConfig();
    
    // Check if returning from successful payment
    const purchaseId = searchParams.get('purchase_id');
    const status = searchParams.get('status');
    
    if (status === 'success' && purchaseId) {
      // Redirect to workspace creation with purchase_id
      navigate(`/workspace/create?purchase_id=${purchaseId}`, { replace: true });
    }
  }, [searchParams, navigate]);

  const fetchRazorpayConfig = async () => {
    try {
      const response = await axios.get(`${API}/razorpay/config`);
      setRazorpayConfig(response.data);
    } catch (error) {
      console.error('Failed to fetch Razorpay config:', error);
      toast.error('Failed to load pricing information');
    }
  };

  const handlePurchase = async (workspaceType) => {
    if (!razorpayConfig?.key_id) {
      toast.error('Payment system is not configured');
      return;
    }

    setLoading(true);
    setProcessingType(workspaceType);

    try {
      // Load Razorpay script
      const scriptLoaded = await loadRazorpayScript();
      if (!scriptLoaded) {
        toast.error('Failed to load payment gateway');
        setLoading(false);
        setProcessingType(null);
        return;
      }

      // Create subscription — the server resolves plan_pro_v2/plan_studio_v2
      // from the workspace type (single price book).
      const orderResponse = await axios.post(`${API}/razorpay/workspace/create-subscription`, {
        workspace_type: workspaceType,
        billing_cycle: billingCycle
      }, {
        headers: getAuthHeaders()
      });

      const { subscription_id, purchase_id, amount, currency, key_id, amount_display } = orderResponse.data;

      // Open Razorpay checkout for subscription
      const options = {
        key: key_id,
        subscription_id: subscription_id,
        name: 'FrameFlow',
        description: `${workspaceType === 'studio' ? 'Studio' : 'Pro'} Workspace - ${billingCycle === 'yearly' ? 'Yearly' : 'Monthly'} Subscription`,
        handler: async (response) => {
          try {
            const verifyResponse = await axios.post(`${API}/razorpay/workspace/verify-subscription`, {
              razorpay_subscription_id: response.razorpay_subscription_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
              purchase_id: purchase_id
            }, {
              headers: getAuthHeaders()
            });

            if (verifyResponse.data.status === 'success') {
              toast.success(verifyResponse.data.message || 'Subscription successful!');
              // Redirect to workspace creation
              navigate(`/workspace/create?purchase_id=${purchase_id}`);
            } else {
              toast.error('Subscription verification failed');
            }
          } catch (verifyError) {
            console.error('Subscription verification error:', verifyError);
            toast.error(verifyError.response?.data?.detail || 'Subscription verification failed');
          }
          setLoading(false);
          setProcessingType(null);
        },
        prefill: {
          name: user?.name || '',
          email: user?.email || '',
        },
        notes: {
          workspace_type: workspaceType,
          billing_cycle: billingCycle,
          purchase_id: purchase_id
        },
        theme: {
          color: '#F59E0B'
        },
        modal: {
          ondismiss: () => {
            setLoading(false);
            setProcessingType(null);
            toast.info('Subscription cancelled');
          }
        }
      };

      const razorpayInstance = new window.Razorpay(options);
      razorpayInstance.on('payment.failed', (response) => {
        console.error('Payment failed:', response.error);
        toast.error(`Payment failed: ${response.error.description}`);
        setLoading(false);
        setProcessingType(null);
      });
      razorpayInstance.open();

    } catch (error) {
      console.error('Purchase error:', error);
      toast.error(error.response?.data?.detail || 'Failed to start checkout');
      setLoading(false);
      setProcessingType(null);
    }
  };

  // Single price book: prefer the backend's live prices. `workspace_prices`
  // now carries the current prices; `workspace_prices_v2` is read first only
  // for deploy-skew tolerance against an older backend. The static constant
  // (same v2 numbers) is the offline fallback — v1 prices no longer exist
  // anywhere in this chain.
  const getPriceBook = (type) => {
    const v2 = razorpayConfig?.workspace_prices_v2?.[type];
    if (v2) return v2;
    if (razorpayConfig?.workspace_prices?.[type]) return razorpayConfig.workspace_prices[type];
    return WORKSPACE_PRICES[type];
  };

  const getPrice = (type, cycle) => {
    const book = getPriceBook(type);
    return book?.[cycle] ?? 0;
  };

  const getMonthlyEquivalent = (type) => {
    const book = getPriceBook(type);
    if (book?.monthly_equivalent_yearly != null) return book.monthly_equivalent_yearly;
    if (book?.yearly) return Math.round(book.yearly / 12);
    return 0;
  };

  // Yearly savings percentage vs 12x monthly, computed live so the pill
  // always tracks the active price book (v2 pro is ~16.6%, v2 studio is ~16.7%).
  const yearlySavingsPct = (() => {
    const proBook = getPriceBook('pro');
    if (!proBook?.monthly || !proBook?.yearly) return 10; // safe fallback
    const twelveX = proBook.monthly * 12;
    if (twelveX <= 0) return 0;
    const pct = Math.round(((twelveX - proBook.yearly) / twelveX) * 100);
    return pct > 0 ? pct : 0;
  })();

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-IN').format(Math.round(price));
  };

  // Show loading while checking for pending purchases
  if (checkingPending) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4 text-primary" />
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-background">
      <main className="container mx-auto px-4 md:px-6 py-8 md:py-12">
        <div className="flex items-center gap-3 mb-6">
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)} className="-ml-2">
            <ArrowLeft className="h-4 w-4 mr-2" />
            <span className="text-xs font-semibold tracking-wider uppercase">Back</span>
          </Button>
          <div className="flex items-center gap-2">
            <Building2 className="h-6 w-6 text-primary" />
            <h1 className="text-xl md:text-2xl font-bold font-display tracking-tight">Buy a Workspace</h1>
          </div>
        </div>
        {/* Hero Section */}
        <div className="text-center mb-10">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Choose Your Workspace Plan
          </h2>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            Upgrade to a Pro or Studio Workspace to unlock collaboration features, 
            unlimited films, and powerful production tools.
          </p>
        </div>

        {/* Billing Cycle Toggle */}
        <div className="flex justify-center mb-8">
          <div className="inline-flex items-center gap-2 p-1 bg-muted rounded-lg">
            <button
              onClick={() => setBillingCycle('monthly')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                billingCycle === 'monthly'
                  ? 'bg-background text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              Monthly
            </button>
            <button
              onClick={() => setBillingCycle('yearly')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                billingCycle === 'yearly'
                  ? 'bg-background text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              Yearly
              {yearlySavingsPct > 0 && (
                <span className="ml-2 text-xs text-green-600 dark:text-green-400 font-semibold">
                  Save {yearlySavingsPct}%
                </span>
              )}
            </button>
          </div>
        </div>

        {/* Workspace Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
          {WORKSPACE_TYPES.map((workspace) => {
            const Icon = workspace.icon;
            const price = getPrice(workspace.type, billingCycle);
            const monthlyEquiv = getMonthlyEquivalent(workspace.type);
            const isProcessing = processingType === workspace.type;
            
            return (
              <Card 
                key={workspace.type}
                className={`p-6 relative ${
                  workspace.popular ? 'border-primary border-2' : ''
                }`}
                data-testid={`workspace-card-${workspace.type}`}
              >
                {workspace.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <span className="bg-primary text-primary-foreground text-xs font-semibold px-3 py-1 rounded-full">
                      Best Value
                    </span>
                  </div>
                )}
                
                <div className="text-center mb-6">
                  <div className={`inline-flex items-center justify-center w-14 h-14 rounded-full ${workspace.bgColor} mb-4`}>
                    <Icon className={`h-7 w-7 ${workspace.color}`} />
                  </div>
                  <h3 className="text-xl font-bold mb-2">{workspace.name}</h3>
                  <p className="text-sm text-muted-foreground mb-4">{workspace.description}</p>
                  
                  <div className="mb-4">
                    <div className="flex items-center justify-center gap-1">
                      <IndianRupee className="h-5 w-5" />
                      <span className="text-4xl font-bold">{formatPrice(price)}</span>
                    </div>
                    <span className="text-muted-foreground">
                      /{billingCycle === 'yearly' ? 'year' : 'month'}
                    </span>
                    {billingCycle === 'yearly' && (
                      <p className="text-sm text-muted-foreground mt-1">
                        (₹{formatPrice(monthlyEquiv)}/month)
                      </p>
                    )}
                  </div>

                  <Button 
                    className="w-full gap-2" 
                    size="lg"
                    onClick={() => handlePurchase(workspace.type)}
                    disabled={loading}
                    data-testid={`buy-${workspace.type}`}
                  >
                    {isProcessing ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Processing...
                      </>
                    ) : (
                      <>
                        <Sparkles className="h-4 w-4" />
                        Buy {workspace.name}
                      </>
                    )}
                  </Button>
                </div>

                <div className="space-y-3 pt-4 border-t border-border">
                  {workspace.features.map((feature, idx) => (
                    <div key={`feat-${idx}-${(feature || '').slice(0, 32)}`} className="flex items-center gap-2 text-sm">
                      <Check className="h-4 w-4 text-green-500 flex-shrink-0" />
                      <span>{feature}</span>
                    </div>
                  ))}
                </div>
              </Card>
            );
          })}
        </div>

        {/* Info Section */}
        <div className="mt-12 text-center">
          <p className="text-sm text-muted-foreground mb-4">
            Secure payments powered by Razorpay • UPI, Cards, Netbanking accepted
          </p>
          <p className="text-xs text-muted-foreground">
            You already have a free workspace. These are additional paid workspaces for teams.
          </p>
        </div>
      </main>
    </div>
  );
};

export default WorkspacePurchase;
